import type { Location } from 'react-router-dom';
import type { AuthUser } from '../context/AuthContext';
import type { Settings } from '../components/SettingsContext';
import { getCurrentDeviceKind, shouldUseMobileApp } from '../lib/deviceClassification';
import { readEnvBoolean } from '../lib/env';
import { DEFAULT_MOBILE_VIEWPORT_BREAKPOINT, isMobileViewportWidth } from '../hooks/use-is-mobile';
import type { UniversalLogEntry } from '../types/logging';
import { readAuthFetchDebugSnapshot } from '../lib/authFetch';

const SENSITIVE_KEY_PATTERNS = [
  'password',
  'secret',
  'token',
  'auth',
  'session',
  'cookie',
  'key',
  'api',
  'credential',
  'email',
  'phone',
];

const MAX_DEPTH = 5;
const MAX_LIST_ITEMS = 20;
const MAX_STRING_LENGTH = 2000;
const MAX_BYTES = 15000;
const BUG_CORRELATION_KEY = 'rh_bug_report_correlation_id';

type SanitizeOptions = {
  depth?: number;
  seen?: WeakSet<object>;
};

function isSensitiveKey(key: string): boolean {
  const lowered = key.toLowerCase();
  return SENSITIVE_KEY_PATTERNS.some((pattern) => lowered.includes(pattern));
}

function sanitizeValue<T = unknown>(value: T, options: SanitizeOptions = {}): unknown {
  const depth = options.depth ?? 0;
  const seen = options.seen ?? new WeakSet<object>();

  if (depth >= MAX_DEPTH) {
    return '[truncated]';
  }

  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length > MAX_STRING_LENGTH) {
      return `${trimmed.slice(0, MAX_STRING_LENGTH)}…`;
    }
    return trimmed;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    const nextOptions: SanitizeOptions = { depth: depth + 1, seen };
    const result = value.slice(0, MAX_LIST_ITEMS).map((item) => sanitizeValue(item, nextOptions));
    if (value.length > MAX_LIST_ITEMS) {
      result.push('[truncated]');
    }
    return result;
  }

  if (typeof value === 'object') {
    if (seen.has(value as object)) {
      return '[circular]';
    }
    seen.add(value as object);

    const entries = Object.entries(value as Record<string, unknown>);
    const nextOptions: SanitizeOptions = { depth: depth + 1, seen };
    const result: Record<string, unknown> = {};

    for (let index = 0; index < entries.length; index += 1) {
      if (index >= MAX_LIST_ITEMS) {
        result.__truncated__ = true;
        break;
      }
      const [key, nested] = entries[index];
      if (isSensitiveKey(key)) {
        result[key] = '[REDACTED]';
        continue;
      }
      result[key] = sanitizeValue(nested, nextOptions);
    }

    return result;
  }

  if (typeof value === 'function') {
    return '[function]';
  }

  try {
    return `${value}`;
  } catch (error) {
    return '[unserializable]';
  }
}

function enforceSize(payload: Record<string, unknown>): Record<string, unknown> {
  try {
    const encoded = JSON.stringify(payload);
    if (encoded.length <= MAX_BYTES) {
      return payload;
    }
  } catch (error) {
    return { __note__: 'context serialization failed' };
  }

  return {
    ...payload,
    __note__: 'context truncated client-side to respect size limits',
  };
}

function sanitizeAndCap(payload: Record<string, unknown>): Record<string, unknown> {
  const sanitized = sanitizeValue(payload, { depth: 0 });
  if (sanitized && typeof sanitized === 'object' && !Array.isArray(sanitized)) {
    return enforceSize(sanitized as Record<string, unknown>);
  }
  return {
    value: sanitized,
  };
}

function readImportMetaString(key: string): string | undefined {
  if (typeof import.meta === 'undefined') return undefined;
  const env = import.meta.env as Record<string, unknown> | undefined;
  const value = env?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function gatherAppContext(settings?: Settings): Record<string, unknown> {
  return sanitizeAndCap({
    environmentLabel:
      readImportMetaString('VITE_ENVIRONMENT_LABEL') ||
      (typeof import.meta !== 'undefined' ? import.meta.env?.MODE : undefined),
    mode: typeof import.meta !== 'undefined' ? import.meta.env?.MODE : undefined,
    frontendVersion: readImportMetaString('VITE_APP_VERSION'),
    frontendGitSha:
      readImportMetaString('VITE_GIT_SHA') ||
      readImportMetaString('VITE_COMMIT_SHA') ||
      readImportMetaString('VITE_RENDER_GIT_COMMIT'),
    apiBaseOverride: settings?.apiBaseOverride || undefined,
  });
}

function getBugCorrelationId(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const existing = window.localStorage?.getItem(BUG_CORRELATION_KEY);
    if (existing) {
      return existing;
    }
    const generated =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `bug-report-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage?.setItem(BUG_CORRELATION_KEY, generated);
    return generated;
  } catch {
    return undefined;
  }
}

function gatherDeviceContext(): Record<string, unknown> {
  if (typeof window === 'undefined') {
    return {};
  }

  const navigatorInfo = typeof navigator !== 'undefined' ? navigator : undefined;
  const featureEnabled = readEnvBoolean('VITE_MOBILE_APP_ENABLED');
  const deviceKind = getCurrentDeviceKind();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const screenWidth = window.screen?.width;
  const screenHeight = window.screen?.height;
  const isMobileViewport = isMobileViewportWidth(viewportWidth, DEFAULT_MOBILE_VIEWPORT_BREAKPOINT);
  const mobileAppActive = shouldUseMobileApp({
    featureEnabled,
    isMobileViewport,
    deviceKind,
  });

  return sanitizeAndCap({
    deviceKind,
    userAgent: navigatorInfo?.userAgent,
    platform: navigatorInfo?.platform,
    languages: navigatorInfo?.languages,
    language: navigatorInfo?.language,
    online: navigatorInfo ? navigatorInfo.onLine : undefined,
    viewport: {
      width: viewportWidth,
      height: viewportHeight,
      pixelRatio: window.devicePixelRatio,
      isMobileViewport,
      breakpoint: DEFAULT_MOBILE_VIEWPORT_BREAKPOINT,
    },
    screen: {
      width: screenWidth,
      height: screenHeight,
    },
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    touchSupport: {
      maxTouchPoints: navigatorInfo?.maxTouchPoints,
    },
    mobileRouting: {
      featureEnabled,
      mobileAppActive,
    },
    locationHref: window.location?.href,
  });
}

function maskEmail(email: string | undefined): string | undefined {
  if (!email || typeof email !== 'string' || !email.includes('@')) {
    return undefined;
  }
  const [local, domain] = email.split('@');
  if (!local) {
    return `***@${domain}`;
  }
  const keep = Math.min(2, local.length);
  return `${local.slice(0, keep)}***@${domain}`;
}

function gatherUserContext(user: AuthUser | null | undefined): Record<string, unknown> | undefined {
  if (!user) {
    return undefined;
  }
  return sanitizeAndCap({
    id: user.id,
    role: user.role,
    username: user.username,
    display_name: user.display_name,
    email_hint: maskEmail(user.email),
  });
}

function summarizeLogs(entries: UniversalLogEntry[] | undefined): Record<string, unknown> | undefined {
  if (!entries || !entries.length) {
    return undefined;
  }

  const sorted = [...entries].sort((a, b) => b.timestamp - a.timestamp);
  const errors = sorted.filter((entry) => entry.level === 'error').slice(0, 8);
  const warnings = sorted.filter((entry) => entry.level === 'warn').slice(0, 5);
  const latest = sorted.slice(0, 10);

  const coerceEntry = (entry: UniversalLogEntry) =>
    sanitizeAndCap({
      id: entry.id,
      level: entry.level,
      message: entry.message,
      category: entry.category,
      source: entry.source,
      page: entry.page,
      timestamp: new Date(entry.timestamp).toISOString(),
      details: entry.details,
    });

  return {
    recentErrors: errors.map(coerceEntry),
    recentWarnings: warnings.map(coerceEntry),
    latest: latest.map(coerceEntry),
  };
}

function summarizeNetwork(): Record<string, unknown> | undefined {
  const entries = readAuthFetchDebugSnapshot(12);
  if (!entries.length) {
    return undefined;
  }
  return sanitizeAndCap({
    recentFetches: entries,
    recentFailures: entries.filter((entry) => entry.ok === false || Boolean(entry.error)).slice(0, 6),
  });
}

export type CollectBugContextParams = {
  location?: Location | null;
  user?: AuthUser | null;
  settings?: Settings;
  entries?: UniversalLogEntry[];
  extra?: Record<string, unknown>;
};

export function collectBugContext(params: CollectBugContextParams): Record<string, unknown> {
  const route = params.location
    ? `${params.location.pathname}${params.location.search}${params.location.hash}`.trim() || undefined
    : undefined;

  const baseContext: Record<string, unknown> = {
    route,
    timestamp_utc: new Date().toISOString(),
    diagnostics: {
      correlation_id: getBugCorrelationId(),
    },
    app: gatherAppContext(params.settings),
    environment: params.settings
      ? sanitizeAndCap({
          theme: params.settings.theme,
          density: params.settings.density,
          enableDevTools: params.settings.enableDevTools,
          apiBaseOverride: params.settings.apiBaseOverride || undefined,
        })
      : undefined,
    device: gatherDeviceContext(),
    user: gatherUserContext(params.user),
    feature_flags: params.settings
      ? sanitizeAndCap({
          enableDevTools: params.settings.enableDevTools,
        })
      : undefined,
    logs: summarizeLogs(params.entries),
    network: summarizeNetwork(),
    extra: params.extra ? sanitizeAndCap(params.extra) : undefined,
  };

  return sanitizeAndCap(baseContext);
}

export function formatBugContextForDisplay(context: Record<string, unknown>): string {
  try {
    return JSON.stringify(context, null, 2);
  } catch (error) {
    return 'Unable to display context payload.';
  }
}
